/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { Link } from "../components/ui/Link";
import { ChevronRight, Lock, Search, Users } from "lucide-react";
import {
  ACCOUNT_STATUSES,
  searchAccounts,
  type AccountScope,
  type AccountSummary,
} from "../api/accounts";
import { PAGE_SIZE } from "../api/registry";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { InfiniteList } from "../components/ui/InfiniteList";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { fullName, roleLabel, statusLabel, statusTone } from "../lib/accounts";
import { ACCOUNT_SORTS, sortChoice } from "../lib/sorting";
import { debounce } from "../lib/utils";

const DEBOUNCE_MILLIS = 300;

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  ...ACCOUNT_STATUSES.map((status) => ({ value: status, label: statusLabel(status) })),
];

/**
 * The one people directory, mounted behind three doorways: Families (scope "clients"), Care →
 * Care team (scope "care_team") and Admin → Access (scope "internal"). Same cards, same detail
 * actions; only the audience and the role filter differ, so staff never wonder which list a
 * person lives in.
 */
export function AccountsDirectory({
  scope,
  roleOptions,
  searchPlaceholder,
}: {
  scope: AccountScope;
  roleOptions: string[];
  searchPlaceholder: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const role = searchParams.get("role") ?? "";
  const sort = sortChoice(ACCOUNT_SORTS, searchParams.get("sort") ?? "");

  const [draft, setDraft] = useState(q);
  const [items, setItems] = useState<AccountSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyFilter = (updates: Record<string, string>) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(updates)) {
          if (value === "") {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      },
      { replace: true },
    );
  };

  // one debounce for the component's lifetime; recreating it per render would drop pending calls
  const debouncedQuery = useMemo(
    () => debounce((value: string) => applyFilter({ q: value }), DEBOUNCE_MILLIS),
    [],
  );
  useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery]);

  // Every filter change resets to page one and REPLACES the list; loadPage(n>1) APPENDS. The
  // sequence counter drops stale responses; the id set drops rows already shown, so a row that
  // shifts pages while the user scrolls (someone registered mid-scroll) never renders twice.
  const requestSeq = useRef(0);
  const loadPage = (pageToLoad: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    void searchAccounts({ q, status, role, scope, page: pageToLoad, pageSize: PAGE_SIZE, sort }).then(
      (response) => {
        if (seq !== requestSeq.current) {
          return;
        }
        setLoading(false);
        if (!response.ok) {
          setError(response.error.message);
          return;
        }
        setError(null);
        setTotal(response.value.total);
        setPage(pageToLoad);
        if (pageToLoad === 1) {
          setItems(response.value.items);
          return;
        }
        setItems((previous) => {
          const seen = new Set((previous ?? []).map((item) => item.id));
          return [
            ...(previous ?? []),
            ...response.value.items.filter((item) => !seen.has(item.id)),
          ];
        });
      },
    );
  };
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    setItems(null);
    loadPageRef.current(1);
  }, [q, status, role, scope, sort]);

  const hasMore = items !== null && items.length < total;

  const roleSelectOptions = [
    { value: "", label: "Any role" },
    ...roleOptions.map((name) => ({ value: name, label: roleLabel(name) })),
  ];

  return (
    <div className="space-y-8">
      <div
        className="enter flex w-full flex-col gap-3 sm:flex-row sm:items-center"
        style={{ "--enter-delay": "80ms" } as CSSProperties}
      >
        <Input
          className="sm:max-w-xl sm:flex-1"
          aria-label="Search accounts"
          placeholder={searchPlaceholder}
          prefix={<Search size={15} className="text-text-tertiary" />}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            debouncedQuery(event.target.value);
          }}
        />
        <Select
          className="sm:ml-auto sm:w-48"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(value) => applyFilter({ status: value })}
        />
        <Select
          className="sm:w-48"
          value={role}
          options={roleSelectOptions}
          onChange={(value) => applyFilter({ role: value })}
        />
          <Select
            className="w-full sm:w-44"
            value={sort.value}
            options={ACCOUNT_SORTS}
            onChange={(value) => applyFilter({ sort: value })}
            aria-label="Sort accounts"
          />
      </div>

      {items === null && error !== null ? (
        <ErrorMessage message={error} />
      ) : items === null ? (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-text-tertiary" aria-live="polite">
            {total === 1 ? "1 account" : `${total} accounts`}
            {loading && <Spinner size="xs" className="ml-2 inline-block align-middle" />}
          </p>
          <InfiniteList
            isLoading={loading}
            hasMore={hasMore}
            onLoadMore={() => loadPageRef.current(page + 1)}
            isEmpty={items.length === 0}
            currentPage={page}
            error={error}
            onRetry={() => loadPageRef.current(page + 1)}
            emptyState={
              <EmptyState
                icon={<Users size={20} />}
                tone={q === "" ? "quiet" : "search"}
                title={q === "" ? "No accounts yet" : "No accounts match this search"}
                description="Try another name, email, or phone."
                actionLabel={q === "" ? undefined : "Clear search"}
                onAction={
                  q === ""
                    ? undefined
                    : () => {
                        setDraft("");
                        applyFilter({ q: "" });
                      }
                }
              />
            }
          >
            <ol className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {items.map((account, index) => (
                <AccountCard
                  key={account.id}
                  account={account}
                  index={index}
                />
              ))}
            </ol>
          </InfiniteList>
        </div>
      )}
    </div>
  );
}

function AccountCard({
  account,
  index,
}: {
  account: AccountSummary;
  index: number;
}) {
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-xl leading-tight text-text-primary">
            <Link
              to={`/accounts/${account.id}`}
              className="after:absolute after:inset-0 after:rounded-3xl"
            >
              {fullName(account.firstName, account.lastName)}
            </Link>
          </h2>
          <p className="mt-0.5 truncate text-xs text-text-tertiary">
            {account.roles.length === 0 ? "No role yet" : account.roles.map(roleLabel).join(" · ")}
            {" · "}
            {account.email ?? account.username ?? account.phone ?? `Account ${account.id}`}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Badge tone={statusTone(account.status)}>{statusLabel(account.status)}</Badge>
          {account.lockedOut && (
            <Badge tone="error">
              <Lock size={10} className="mr-1 inline-block" />
              Locked out
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
        {account.familyId !== null ? (
          <Link
            to={`/care/families/${account.familyId}`}
            className="relative z-10 inline-flex items-center gap-1.5 text-text-tertiary transition-colors hover:text-accent"
          >
            <Users size={12} />
            Family {account.familyId}
          </Link>
        ) : (
          <span />
        )}
        <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
          Open
          <ChevronRight
            size={14}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </span>
      </div>
    </li>
  );
}
